import {
  Alert,
  CircularProgress,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import {Stack} from "@mui/system";
import {CustomModal, ModalWrapperFooter} from "components";
import useAlertMessage from "hooks/useAlertMessage";
import pxToRem from "hooks/usePxToRem";
import useToast from "hooks/useToast";
import {ReactNode, useMemo, useState} from "react";
import {PostContainersWebhook} from "services/n8n/ContainersWebhook.service";
import {LogisticBillLadingLoads} from "views/ProcessDetails/refactor/types/knowledges.types";
import {TypeItemLoad} from "util/globalEnums";
import {CustomButton} from "ui";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  slotsCount: number;
  renderItem: (index: number) => ReactNode;
  setHouseMeasurements: () => void;
  setIsBulkInsertionConfirmed: (value: boolean) => void;
  onConfirmBulkInsertion: () => void;
  importedItemsIndexes: number[];
  setImportedItemsIndexes: (value: number[]) => void;
  applyImportedItems: (
    items: Record<string, unknown>[],
    maxIndexToReplace: number,
  ) => void;
};

const BulkInsertionModal = (props: Props) => {
  const {
    isOpen,
    onClose,
    slotsCount,
    renderItem,
    setHouseMeasurements,
    setIsBulkInsertionConfirmed,
    onConfirmBulkInsertion,
    importedItemsIndexes,
    setImportedItemsIndexes,
    applyImportedItems,
  } = props;

  const [showWarningAlert, setShowWarningAlert] = useState(true);
  const [bulkInsertionData, setBulkInsertionData] = useState("");
  const [isSendingBulkInsertionData, setIsSendingBulkInsertionData] =
    useState(false);

  const [showToast] = useToast();
  const [showAlert] = useAlertMessage();

  const mapApiItemToCargoItem = (
    item: LogisticBillLadingLoads,
  ): Record<string, unknown> => {
    return {
      id: item?.id,
      typeItemLoad: TypeItemLoad.Container,
      number: item?.number ?? "",
      seal: item?.seal ?? "",
      barSeal: item?.barSeal ?? "",
      equipment: item?.equipment
        ? {id: item?.equipment?.id, name: item?.equipment?.name}
        : null,
      quantity: item?.quantity ?? 0,
      packageContainer: item?.packageContainer
        ? {id: item?.packageContainer?.id, name: item?.packageContainer?.name}
        : null,
      netWeight: 0,
      grossWeight: item?.grossWeight ?? 0,
      measurement: item?.measurement ?? 0,
      tare: item?.tare ?? 0,
      dangerousGoods: item?.dangerousGoods ?? "",
      dangerousGoodsClass: item?.dangerousGoodsClass ?? "",
      dangerousGoodsCode: item?.dangerousGoodsCode ?? "",
    };
  };

  const importedSlots = useMemo(
    () =>
      importedItemsIndexes
        ?.map((index) => ({index}))
        ?.filter((item) => item?.index >= 0 && item?.index < slotsCount),
    [importedItemsIndexes, slotsCount],
  );

  const handleBulkImport = async () => {
    if (!bulkInsertionData.trim()) {
      showToast(
        "Insira os dados para adicionar itens de carga em lote.",
        "warning",
      );
      return;
    }

    if (slotsCount === 0) {
      showToast("Não há itens de carga para substituir.", "warning");
      return;
    }

    try {
      setIsSendingBulkInsertionData(true);
      const {data} = await PostContainersWebhook({data: bulkInsertionData});

      if (!Array.isArray(data) || data.length === 0) {
        showToast(
          "Nenhum item de carga foi encontrado para importação.",
          "warning",
        );
        setImportedItemsIndexes([]);
        return;
      }

      const mappedItems = data.map(mapApiItemToCargoItem);
      const maxIndexToReplace = Math.min(mappedItems.length, slotsCount);

      if (mappedItems.length > slotsCount) {
        showToast(
          "Alguns itens importados foram ignorados por excederem a quantidade existente.",
          "warning",
        );
      }

      applyImportedItems(mappedItems, maxIndexToReplace);

      const replacedIndexes = Array.from(
        {length: maxIndexToReplace},
        (_, i) => i,
      );

      setImportedItemsIndexes(replacedIndexes);
      setHouseMeasurements();
      setIsBulkInsertionConfirmed(false);
      showToast("Itens importados. Revise e clique em OK.", "success");
    } catch (error: unknown) {
      const errorMessage =
        (error as {response?: {data?: {message?: string}}})?.response?.data
          ?.message ?? "Não foi possível importar os itens de carga.";
      console.error(error);
      showAlert(errorMessage);
    } finally {
      setIsSendingBulkInsertionData(false);
    }
  };

  const handleConfirmBulkInsertion = () => {
    if (importedItemsIndexes.length === 0) return;
    onConfirmBulkInsertion();
    setImportedItemsIndexes([]);
    setBulkInsertionData("");
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Inserir itens em lote"
      titleIcon="android"
      maxWidth="md"
      dataTestId="bulk-cargo-items-modal"
    >
      <DialogContent>
        <Stack sx={{gap: pxToRem(12)}}>
          {showWarningAlert && (
            <Alert
              severity="warning"
              sx={{mt: pxToRem(8)}}
              onClose={() => setShowWarningAlert(false)}
            >
              <Typography>
                Pré-preenchimento feito por IA, os campos devem ser verificados
                e corrigidos.
              </Typography>
            </Alert>
          )}
          <TextField
            multiline
            rows={8}
            fullWidth
            label="Dados dos itens"
            placeholder="Cole aqui os dados para importação"
            value={bulkInsertionData}
            onChange={(e) => setBulkInsertionData(e.target.value)}
            slotProps={{
              htmlInput: {"data-testid": "bulk-cargo-items-textarea"},
            }}
          />

          {importedSlots?.length > 0 && (
            <Stack sx={{gap: pxToRem(8)}}>
              <Typography variant="h6">Itens importados</Typography>
              {importedSlots?.map(({index}) => renderItem(index))}
            </Stack>
          )}
        </Stack>

        <Alert severity="warning" sx={{mt: pxToRem(8)}}>
          <Typography>
            Os itens importados substituem os itens existentes, se inserir 2
            novos itens, os 2 primeiros itens serão substituídos.
          </Typography>
        </Alert>
      </DialogContent>
      <ModalWrapperFooter>
        <Stack
          direction="row"
          justifyContent="flex-end"
          width="100%"
          gap={pxToRem(8)}
        >
          <CustomButton
            variant="outlined"
            onClickFn={onClose}
            disabled={isSendingBulkInsertionData}
          >
            Cancelar
          </CustomButton>
          {importedSlots?.length === 0 ? (
            <CustomButton
              onClickFn={handleBulkImport}
              disabled={
                !bulkInsertionData?.trim() || isSendingBulkInsertionData
              }
            >
              {isSendingBulkInsertionData ? (
                <Stack direction="row" alignItems="center" gap={pxToRem(4)}>
                  <CircularProgress size={16} color="inherit" />
                  Importando...
                </Stack>
              ) : (
                "Importar"
              )}
            </CustomButton>
          ) : (
            <CustomButton
              onClickFn={handleConfirmBulkInsertion}
              disabled={isSendingBulkInsertionData}
            >
              OK
            </CustomButton>
          )}
        </Stack>
      </ModalWrapperFooter>
    </CustomModal>
  );
};

export default BulkInsertionModal;
